import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../config';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { isValidPhone } from '../../common/validators';
import { toast } from 'react-toastify';

function StaffModal({ show, onHide, onStaffAdded, editData = null }) {
    const [form, setForm] = useState({
        name: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        roleId: '',
        telegramId: '',
    }); 

    useEffect(() => {
        if (editData) {
            setForm({
                name: editData.name || '',
                phone: editData.phone || '',
                email: editData.email || '',
                password: '', // Password is empty when editing unless changed
                confirmPassword: '',
                roleId: editData.roleId?._id || editData.roleId || '',
                telegramId: editData.telegramId || '',
            });
        } else {
            setForm({
                name: '',
                phone: '',
                email: '',
                password: '',
                confirmPassword: '',
                roleId: '',
                telegramId: '',
            });
        }
        setTouched({});
    }, [editData, show]);

    const [touched, setTouched] = useState({});
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        axios.get(`${config.API_BASE_URL}/roles`)
            .then((res) => setRoles(res.data))
            .catch((err) => console.error('Error loading roles:', err));
    }, []);

    const handleChange = e => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async e => {
        e.preventDefault();

        // Validation for password if provided
        if (form.password || !editData) {
            if (form.password.length < 6) {
                toast.error('Password must be at least 6 characters long');
                return;
            }
            if (form.password !== form.confirmPassword) {
                toast.error('Passwords do not match');
                return;
            }
        }

        try {
            if (editData) {
                // Update
                const updatePayload = { ...form };
                if (!updatePayload.password) {
                    delete updatePayload.password;
                    delete updatePayload.confirmPassword;
                }
                await axios.put(`${config.API_BASE_URL}/staff/${editData._id}`, updatePayload);
                toast.success('Staff updated successfully!');
            } else {
                // Create
                await axios.post(`${config.API_BASE_URL}/staff`, form);
                toast.success('Staff added successfully!');
            }
            onStaffAdded();
            onHide();
        } catch (err) {
            console.error("Staff operation failed", err);
            toast.error(err.response?.data?.message || 'Operation failed!');
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>{editData ? 'Edit Staff' : 'Add Staff'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        {/* Render fields */}
                        {[
                            { label: 'Staff Name', name: 'name' },
                            { label: 'Phone', name: 'phone' },
                            { label: 'Email', name: 'email' },
                            // { label: 'Telegram User ID', name: 'telegramId' },
                            { label: 'Password', name: 'password' },
                        ].map(({ label, name, as }) => (
                            <Col md={6} key={name}>
                                <Form.Group className="mb-2">
                                    <Form.Label>{label}</Form.Label>
                                    {name === 'phone' ? (
                                        <>
                                            <PhoneInput
                                                country="in"
                                                value={form.phone}
                                                onChange={(value) =>
                                                    setForm({ ...form, phone: `+${value}` })
                                                }
                                                onBlur={() =>
                                                    setTouched((prev) => ({ ...prev, phone: true }))
                                                }
                                                inputProps={{ name: 'phone', required: true }}
                                                inputClass={
                                                    touched.phone &&
                                                        form.phone &&
                                                        !isValidPhone(form.phone)
                                                        ? 'is-invalid'
                                                        : ''
                                                }
                                            />
                                            {touched.phone &&
                                                form.phone &&
                                                !isValidPhone(form.phone) && (
                                                    <div className="invalid-feedback d-block">
                                                        Enter a valid phone number with country code
                                                    </div>
                                                )}
                                        </>
                                    ) : (
                                        <Form.Control
                                            type={name === 'password' ? 'password' : 'text'}
                                            name={name}
                                            value={form[name]}
                                            onChange={handleChange}
                                            onBlur={() =>
                                                setTouched((prev) => ({ ...prev, [name]: true }))
                                            }
                                            isInvalid={
                                                touched[name] &&
                                                name === 'password' &&
                                                form.password && 
                                                form.password.length < 6
                                            }
                                            required={
                                                name === 'password' 
                                                    ? !editData 
                                                    : (name === 'telegramId' ? false : true)
                                            }
                                        />
                                    )}
                                    {name === 'password' && editData && (
                                        <small className="text-muted">Leave blank to keep current password</small>
                                    )}
                                    {name === 'password' && touched.password && form.password && form.password.length < 6 && (
                                        <Form.Control.Feedback type="invalid">
                                            Password must be at least 6 characters long.
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </Col>
                        ))}

                        {/* Confirm Password */}
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Confirm Password</Form.Label>
                                <Form.Control
                                    type="password"
                                    name="confirmPassword"
                                    value={form.confirmPassword}
                                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                                    onBlur={() =>
                                        setTouched((prev) => ({ ...prev, confirmPassword: true }))
                                    }
                                    isInvalid={
                                        touched.confirmPassword &&
                                        form.confirmPassword !== form.password
                                    }
                                    required={!!form.password || !editData}
                                />
                                {touched.confirmPassword &&
                                    form.confirmPassword !== form.password && (
                                        <Form.Control.Feedback type="invalid">
                                            Passwords do not match.
                                        </Form.Control.Feedback>
                                    )}
                            </Form.Group>
                        </Col>

                        {/* Role Selector */}
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label>Role</Form.Label>
                                <Form.Select
                                    name="roleId"
                                    value={form.roleId}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select Role</option>
                                    {roles.map((role) => (
                                        <option key={role._id} value={role._id}>
                                            {role.role_name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit">{editData ? 'Update Staff' : 'Add Staff'}</Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default StaffModal;
